import { combineReducers } from 'redux';
import { getFormValues, autofill, startSubmit, stopSubmit } from 'redux-form';
import axios from 'axios';
import _ from 'lodash';
import i18next from 'i18next';

import { CLIENT_ALLOWANCE_FORM } from 'src/constants/FormNames';
import * as Urls from 'src/constants/EndpointUrls';
import { concatParamsToUrl, redirectTo } from 'src/utils/Http';
import { getClientAllowanceFormat } from 'src/utils/Motivation';
import { setList, getList } from 'src/reducers/common/listReducer';
import { setGlobalErrors, setGlobalSuccesses } from 'src/reducers/global';
import { getTutorialStep2, setTutorialStep2 } from 'src/utils/Tutorial';
import { scrollToTop } from 'src/utils/Utils';

import createNamedWrapperReducer from '../createNamedWrapperReducer';
import { listReducer, itemReducer } from '../common';
import { newItemFailure, editItemFailure } from '../common/itemReducer';

export const REDUCER_NAME = 'clientAllowances';

export const setClientAllowances = data => setList(REDUCER_NAME, { data });
export const getClientAllowances = state => getList(REDUCER_NAME, state);
export const getAllowanceFormValues = state => getFormValues(CLIENT_ALLOWANCE_FORM)(state);
export const updateFormField = (fieldName, value) => autofill(CLIENT_ALLOWANCE_FORM, fieldName, value);

export const createAllowance = data => async dispatch => {
  try {
    dispatch(startSubmit(CLIENT_ALLOWANCE_FORM));

    const isMotivationPayment = data.paymentType === 'motivation';

    const allowance = getClientAllowanceFormat(data, isMotivationPayment)(dispatch);

    if (!allowance) return;

    const createUrl = concatParamsToUrl(Urls.CLIENT_ALLOWANCE_CREATE, {
      payrollRulesGroupId: data.clientPayrollRulesGroupId
    });

    const response = await axios.post(createUrl, allowance);

    if (response.data.errors.messages.length > 0) {
      dispatch(stopSubmit(CLIENT_ALLOWANCE_FORM));
      dispatch(newItemFailure(REDUCER_NAME, response.data.errors.messages));
    } else {
      redirectTo(concatParamsToUrl(Urls.CLIENT_ALLOWANCES_LIST, { payrollRulesGroupId: data.clientPayrollRulesGroupId }));
    }
    dispatch(stopSubmit(CLIENT_ALLOWANCE_FORM));
  } catch (exception) {
    dispatch(stopSubmit(CLIENT_ALLOWANCE_FORM));
    if (exception.response) {
      dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
    } else {
      dispatch(editItemFailure(REDUCER_NAME, [exception.message]));
    }
  }
};

export const editAllowance = data => async dispatch => {
  try {
    dispatch(startSubmit(CLIENT_ALLOWANCE_FORM));

    const isMotivationPayment = data.paymentType === 'motivation';

    const allowance = getClientAllowanceFormat(data, isMotivationPayment)(dispatch);
    const { client_allowance } = allowance;

    if (!allowance) return;

    if (client_allowance?.formulaError) {
      dispatch(stopSubmit(CLIENT_ALLOWANCE_FORM));
      dispatch(newItemFailure(REDUCER_NAME, [i18next.t('reducers.allowances.form_of_salary_is_incorrect')]));
      scrollToTop();
      return;
    }

    const editUrl = concatParamsToUrl(Urls.CLIENT_ALLOWANCE_UPDATE, {
      payrollRulesGroupId: data.clientPayrollRulesGroupId,
      allowanceId: data.id
    });

    const response = await axios.put(editUrl, allowance);
    if (response.data.errors.messages.length > 0) {
      dispatch(stopSubmit(CLIENT_ALLOWANCE_FORM));
      dispatch(editItemFailure(REDUCER_NAME, response.data.errors.messages));
      scrollToTop();
    } else {
      redirectTo(concatParamsToUrl(Urls.CLIENT_ALLOWANCES_LIST, { payrollRulesGroupId: data.clientPayrollRulesGroupId }));
      const tutorialStep2 = getTutorialStep2();
      if (tutorialStep2) {
        setTutorialStep2({ run: false, stepIndex: 0, tourCompleted: true, type: 'next' });
      }
    }
  } catch (exception) {
    dispatch(stopSubmit(CLIENT_ALLOWANCE_FORM));
    if (exception.response) {
      dispatch(editItemFailure(REDUCER_NAME, exception.response.data.errors.messages));
      scrollToTop();
    } else {
      dispatch(editItemFailure(REDUCER_NAME, [exception.message]));
    }
  }
};

export const deleteAllowance = (payrollId, allowanceId) => {
  const deleteAllowanceUrl = concatParamsToUrl(Urls.CLIENT_ALLOWANCE_DELETE, {
    payrollRulesGroupId: payrollId,
    allowanceId
  });
  return axios.delete(deleteAllowanceUrl);
};

const toDisplayOrderParams = clientAllowances => clientAllowances.map((item, index) => ({ id: item.id, displayOrder: index }));

export const saveDisplayOrder = payrollRulesGroupId => async (dispatch, getState) => {
  try {
    const clientAllowances = getClientAllowances(getState());
    const requestParams = toDisplayOrderParams(clientAllowances);
    await axios.post(Urls.CLIENT_ALLOWANCE_UPDATE_DISPLAY_ORDERS, { allowances: requestParams });
    dispatch(setGlobalSuccesses(i18next.t('reducers.allowances.the_sorting_was_successful')));
    redirectTo(concatParamsToUrl(Urls.CLIENT_ALLOWANCES_LIST, { payrollRulesGroupId }));
  } catch (exception) {
    const errorMsg = _.get(exception, 'response.data.errors.messages', i18next.t('reducers.allowances.the_sorting_failed'));
    dispatch(setGlobalErrors(errorMsg));
  }
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME)
});
